import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import FancyDrink from 'assets/fancy-drink.webp';
import LogoAutoRules from 'assets/autorules-logo-white.svg?react';
import { useUsersList } from '../api';
import { Auth } from 'aws-amplify';
import LogoutIcon from '@mui/icons-material/Logout';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import copy from 'copy-to-clipboard';
import useEnqueueSnackbar from '../../../hooks/useEnqueueSnackbar';
import type { FC } from 'react';
import { useModal } from '../../../hooks';
import { styled } from '@mui/material/styles';

const Background = styled(Box)`
  background: url(${FancyDrink}) no-repeat center center;
  background-size: cover;
  display: flex;
  width: 50%;
  border-radius: 12px;
  position: relative;
  flex-direction: column;
  justify-content: space-between;
  padding: 20px;
  ${({ theme: t }) => t.breakpoints.down('md')} {
    display: none;
  }
  ${({ theme: t }) => t.breakpoints.down('lg')} {
    width: 100%;
  }
  ,
`;

interface ListItemProps {
  email: string;
}

const CustomListItem: FC<ListItemProps> = ({ email }) => {
  const { onShowAlert } = useEnqueueSnackbar();
  const { open: setIsHovered, close: setIsNotHovered, isOpen: isHovered } = useModal();

  return (
    <Box onMouseEnter={setIsHovered} onMouseLeave={setIsNotHovered}>
      <ListItem
        sx={{ py: 1.5, width: '100%', display: 'flex', justifyContent: 'space-between', px: 0, height: '48px' }}
      >
        <Typography variant="body1">{email}</Typography>
        {isHovered && (
          <Button
            sx={{ height: '36px' }}
            onClick={() => {
              copy(email);
              onShowAlert('Copied successfully');
            }}
          >
            <ContentCopyIcon sx={{ mr: 0.5 }} />
            Copy email
          </Button>
        )}
      </ListItem>
      <Divider component="li" sx={{ borderColor: 'black' }} />
    </Box>
  );
};

const Forbidden = () => {
  const handleSignOut = async () => {
    await Auth.signOut();
  };

  const { data: users } = useUsersList({
    params: {
      role: ['supervisor'],
      offset: 0,
      limit: 50,
    },
  });

  return (
    <Box
      sx={(t) => ({
        display: 'flex',
        flexGrow: 1,
        width: '100%',
        flexDirection: 'column',
        height: '100vh',
        p: 1.5,
        backgroundColor: 'rgba(156, 39, 176, 0.04)',
        gap: 3,
        [t.breakpoints.up('lg')]: {
          flexDirection: 'row',
        },
      })}
    >
      <Background>
        <LogoAutoRules width={139} height={46} />
        <Typography fontSize={64} color="white" sx={{ width: '346px' }}>
          Campaigns automation
        </Typography>
      </Background>
      <Box
        sx={(t) => ({
          width: '50%',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          [t.breakpoints.down('lg')]: {
            width: '100%',
          },
        })}
      >
        <Box
          sx={{
            backgroundColor: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 4,
            px: 3.5,
            borderRadius: 1.5,
          }}
        >
          <Typography variant="h3" fontSize={24} color="gray">
            No access
          </Typography>
          <Button
            variant="text"
            size="large"
            component="button"
            onClick={handleSignOut}
            startIcon={<LogoutIcon />}
            sx={(t) => ({
              mr: 1,
              color: t.palette.text.primary,
              fontWeight: 500,
              textTransform: 'uppercase',
              m: 0,
              height: '36px',
            })}
          >
            log out
          </Button>
        </Box>
        <Box
          sx={(t) => ({
            width: '100%',
            borderRadius: 1.5,
            overflowY: 'scroll',
            backgroundColor: 'white',
            py: 4,
            px: 3.5,
            [t.breakpoints.down('lg')]: { height: '600px' },
            [t.breakpoints.down('lg')]: { height: '750px' },
          })}
        >
          <Typography fontSize={34} sx={{ mb: 1.5 }}>
            Contact any project member to request the access
          </Typography>
          <List sx={{ p: 0 }}>
            {users.data.map(({ email, id }) => (
              <CustomListItem email={email} key={id} />
            ))}
          </List>
        </Box>
      </Box>
    </Box>
  );
};

export default Forbidden;
